/**
 * Shared property-panel search helpers (query matching + React context).
 */

import { Divider } from "@mui/material";
import React, { createContext, useContext } from "react";

const PropertySearchContext = createContext("");

/**
 * Provides the raw search query to property panel sections.
 */
export const PropertySearchProvider: React.FC<{
  query: string;
  children: React.ReactNode;
}> = ({ query, children }) => (
  <PropertySearchContext.Provider value={query}>
    {children}
  </PropertySearchContext.Provider>
);

/**
 * Returns the current property-panel search query (raw string from the input).
 */
export function usePropertySearchQuery(): string {
  return useContext(PropertySearchContext);
}

/**
 * Returns true when there is no query, or when any label contains the query
 * (case-insensitive).
 *
 * @param query - Raw search string.
 * @param labels - Candidate labels (property names, section/subcategory titles).
 */
export function matchesPropertySearch(
  query: string,
  ...labels: Array<string | null | undefined>
): boolean {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return true;
  return labels.some((label) =>
    String(label ?? "")
      .toLocaleLowerCase()
      .includes(normalized),
  );
}

/**
 * Collects string labels from a dictionary for the given keys.
 *
 * @param source - Label dictionary (layout editor properties, etc.).
 * @param keys - Keys to read.
 */
export function collectLabelValues(
  source: Record<string, unknown> | undefined,
  keys: string[],
): string[] {
  if (!source) return [];
  const values: string[] = [];
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.length > 0) {
      values.push(value);
    }
  }
  return values;
}

/**
 * Collects string labels whose keys start with one of the given prefixes.
 *
 * @param source - Label dictionary.
 * @param prefixes - Key prefixes to include (e.g. `barLoader`, `list`).
 */
export function collectLabelValuesByPrefix(
  source: Record<string, unknown> | undefined,
  prefixes: string[],
): string[] {
  if (!source) return [];
  const values: string[] = [];
  for (const [key, value] of Object.entries(source)) {
    if (typeof value !== "string" || value.length === 0) continue;
    if (prefixes.some((prefix) => key === prefix || key.startsWith(prefix))) {
      values.push(value);
    }
  }
  return values;
}

/**
 * Renders children only when the search query matches one of the labels.
 * Optionally prefixes a divider when visible.
 */
export const PropertySearchableSection: React.FC<{
  labels: Array<string | null | undefined>;
  showTopDivider?: boolean;
  children: React.ReactNode;
}> = ({ labels, showTopDivider = false, children }) => {
  const query = usePropertySearchQuery();
  if (!matchesPropertySearch(query, ...labels)) {
    return null;
  }
  return (
    <>
      {showTopDivider ? <Divider sx={{ my: 1.5 }} /> : null}
      {children}
    </>
  );
};
